import { writeRoster } from './rosterWrite.js'
/**
 * API client for the Southern Recognition backend (sr-portal-backend).
 *
 * Rules of the road:
 *  - Every data request goes through this module. Nothing else builds URLs
 *    or attaches auth headers.
 *  - HubSpot is NEVER called from the browser. We only ever talk to
 *    api.srchamp.com, which brokers HubSpot server-side.
 *  - The only credential the client holds is the runtime JWT, kept in
 *    sessionStorage under `sr_session_token`. No secrets are bundled.
 */

import {advanceSessionGeneration, getSessionGeneration} from './sessionGeneration.js'

// `import.meta.env` is injected by Vite and is undefined anywhere else, so it
// is read defensively — otherwise this module cannot even be imported outside
// a Vite build (which is what verify-session.mjs does).
/* SAME-ORIGIN BY DEFAULT. The browser talks to app.srchamp.com and nothing
 * else; Netlify proxies /api/* to the backend server-side (see netlify.toml).
 *
 * WHY THIS CHANGED. The app was calling https://api.srchamp.com directly and
 * Brian could not sign in from his phone while the API was demonstrably
 * healthy — health 200, CORS preflight 204, login returning a clean 401 to a
 * bad-credentials probe from a desktop.
 *
 * The two domains do not resolve the same way:
 *
 *     app.srchamp.com  →  Netlify   A + AAAA  (IPv4 and IPv6)
 *     api.srchamp.com  →  Railway   A only    (no IPv6 at all)
 *
 * A phone on an IPv6-preferring network loads the app over IPv6 and then has
 * nowhere to send the API call. Desktop never sees it because Windows falls
 * back to IPv4. Cache clearing does nothing, which is exactly what happened.
 *
 * Going same-origin removes the whole class of problem: one hostname, one
 * address family, no CORS surface, and no dependency on the backend's DNS
 * ever growing an AAAA record.
 *
 * VITE_API_BASE still overrides for local dev against a remote backend. */
/* A LOCALHOST API BASE IS IGNORED WHEN THIS IS NOT RUNNING ON LOCALHOST.
 *
 * THIS WAS THE BUG. A `VITE_API_BASE=http://localhost:5173` was set on the
 * production Netlify site, so every deployed build told every browser to
 * fetch the API from the DEVICE IT WAS RUNNING ON. On a phone that is simply
 * nothing, so the fetch rejected and the app reported "Network error — could
 * not reach the server" while the real backend was completely healthy.
 *
 * It survived a cache clear, a private tab, an IPv6 theory and a same-origin
 * rewrite, because none of those touched the one line that was wrong. The
 * build stamp is what finally exposed it: `api http://localhost:5173`.
 *
 * The env var is being corrected too, but a dashboard setting is not a place
 * to keep a load-bearing invariant — anyone can set it again. So the code
 * refuses it structurally: a localhost target is only honoured when the page
 * itself is served from localhost, which is the only situation where it can
 * possibly be right.
 */
const RAW_API_BASE = ((import.meta.env && import.meta.env.VITE_API_BASE) || '').replace(/\/+$/, '')

function resolveApiBase(raw) {
  if (!raw) return ''
  const pointsAtLocalhost = /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(:|\/|$)/i.test(raw)
  if (!pointsAtLocalhost) return raw
  const servedFromLocalhost =
    typeof location !== 'undefined' &&
    /^(localhost|127\.0\.0\.1|\[::1\])$/i.test(location.hostname)
  // Served from a real domain but told to call localhost: that is always
  // wrong, and falling back to same-origin is always right.
  return servedFromLocalhost ? raw : ''
}

export const API_BASE = resolveApiBase(RAW_API_BASE)

export const TOKEN_KEY = 'sr_session_token'

/**
 * The real SR order list in ClickUp — the source of truth for the Customer
 * Journey (order tasks, real rep assignees). The browser never talks to
 * ClickUp; the backend brokers it, and this id just tells it which list.
 */
export const CLICKUP_ORDERS_LIST_ID = '901000306889'

/* ---- Token storage ------------------------------------------------- */

/**
 * THE SESSION LIVES IN localStorage, NOT sessionStorage.
 *
 * sessionStorage is scoped to ONE TAB. A rep who opened the Command Center in
 * a second tab — a middle-click, a "duplicate tab", a link from a message — landed
 * there with no token at all, and every write from that tab went out
 * unauthenticated and came back 401. They believed they had saved a champion
 * and nothing had been written.
 *
 * localStorage is shared across tabs of the same origin, so the session
 * follows the rep. Reads still fall back to sessionStorage once, so anyone
 * holding a session from before this change is carried over instead of being
 * logged out, and that value is promoted on first read.
 */
export function getToken() {
  ensureSessionListener()
  try {
    const shared = localStorage.getItem(TOKEN_KEY)
    if (shared) return shared
    // One-time migration for a tab that signed in under the old model.
    const legacy = sessionStorage.getItem(TOKEN_KEY)
    if (legacy) {
      try { localStorage.setItem(TOKEN_KEY, legacy); sessionStorage.removeItem(TOKEN_KEY) } catch { /* ignore */ }
      return legacy
    }
    return null
  } catch {
    return null
  }
}

export function setToken(token) {
  const before = getToken()
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* storage unavailable (private mode / SSR) — auth simply won't persist */
  }
  // Always clear the legacy per-tab copy so the two can never disagree about
  // who is signed in.
  try { sessionStorage.removeItem(TOKEN_KEY) } catch { /* ignore */ }
  if (before !== getToken()) sessionChanged()
}

/**
 * Sign-out in one tab must not leave another tab holding a dead session and
 * writing into a void. Notifies actual same-tab and cross-tab session changes.
 */
const sessionListeners = new Set()
let sessionWindow = null
function sessionChanged() {
  advanceSessionGeneration()
  for (const fn of sessionListeners) {
    try { fn(getToken()) } catch { /* One subscriber must not delay sign-out. */ }
  }
}
function storageSessionChanged(event) {
  if (event.storageArea && event.storageArea !== globalThis.localStorage) return
  if (event.key !== null && event.key !== TOKEN_KEY) return
  if (event.key === TOKEN_KEY && event.oldValue === event.newValue) return
  // Never resurrect a migrated per-tab session after another tab signed out.
  try { sessionStorage.removeItem(TOKEN_KEY) } catch { /* storage unavailable */ }
  sessionChanged()
}
function ensureSessionListener() {
  if (typeof window === 'undefined' || sessionWindow === window) return
  sessionWindow?.removeEventListener('storage', storageSessionChanged)
  sessionWindow = window
  sessionWindow.addEventListener('storage', storageSessionChanged)
}
export function onSessionChange(fn) {
  ensureSessionListener()
  sessionListeners.add(fn)
  return () => sessionListeners.delete(fn)
}

export function clearToken() {
  setToken(null)
}

/* ---- Error type ---------------------------------------------------- */

export class ApiError extends Error {
  constructor(message, { status, body, path } = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
    this.path = path
    // A 404 from the API means the ROUTE is not deployed (the backend 404s
    // before auth — an existing-but-guarded route answers 401). Callers use
    // this to tell "feature not live yet" apart from "request failed".
    this.missingRoute = status === 404
  }
}

/**
 * Express' default 404/500 handler answers with an HTML document. Dumping that
 * into the UI is what turned a missing route into an unreadable wall of markup,
 * so HTML bodies never become user-facing messages.
 */
function looksLikeMarkup(text) {
  return /^\s*(<!doctype|<html|<\?xml)/i.test(String(text || ''))
}

/**
 * ⚠️ A REASON FIELD IS NOT ALWAYS A STRING, AND THAT IS HOW A BUTTON WENT
 * SILENT.
 *
 * `POST /api/rep/google/start` refuses a rep with no roster email like this:
 *
 *     { ok:false, error:'rep_connect_failed',
 *       detail:{ code:'rep_email_missing', detail:'Your roster record has no
 *                email address, so a Gmail grant could not be proved to be
 *                yours. Ask Brian to add it, then connect.' } }
 *
 * The sentence a human needs is RIGHT THERE — one level further down. The old
 * `payload?.detail` handed back the OBJECT, which became the ApiError message
 * ("[object Object]"), got stored in component state, and was then rendered as
 * a React child. React threw #31 — "objects are not valid as a React child,
 * found an object with keys {code, detail}" — the subtree unmounted, and the
 * Connect card DISAPPEARED. The server had written a perfect explanation and
 * the client destroyed the screen that was supposed to show it.
 *
 * So every candidate prose field is reduced to a STRING here, once, before
 * anything downstream can try to render it. This is not a Gmail fix: any route
 * that nests `{code, detail}` had the same crash waiting.
 *
 * `[object Object]` is deliberately treated as NO PROSE rather than as a
 * message, so the caller falls through to `Request failed (409)` — useless, but
 * visible, which beats a blank card every time.
 */
export function proseOf(v, depth = 0) {
  if (v == null) return ''
  if (typeof v === 'string') return v.trim()
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  if (depth >= 3) return ''
  // An array of reasons: take the first one that yields prose.
  if (Array.isArray(v)) {
    for (const item of v) {
      const s = proseOf(item, depth + 1)
      if (s) return s
    }
    return ''
  }
  if (typeof v === 'object') {
    // The nested shape, in the order a sentence is most likely to live.
    for (const k of ['detail', 'reason', 'message', 'error', 'description']) {
      const s = proseOf(v[k], depth + 1)
      if (s) return s
    }
  }
  return ''
}

/* ---- Write observer (audit hook) ----------------------------------- */

/**
 * A single observer notified after every non-GET request completes. The
 * access layer registers one so the audit trail is STRUCTURAL: any write that
 * goes through this module is observed, whether or not its call site
 * remembered to instrument itself. Adding a new write endpoint below is
 * therefore enough to have it audited.
 *
 * The observer must never throw and must never alter the response — it is
 * fire-and-forget bookkeeping wrapped in a try/catch below.
 */
let writeObserver = null

export function setWriteObserver(fn) {
  writeObserver = typeof fn === 'function' ? fn : null
  return () => {
    if (writeObserver === fn) writeObserver = null
  }
}

function notifyWrite(record) {
  if (!writeObserver) return
  try {
    writeObserver(record)
  } catch {
    /* bookkeeping must never break the request it observes */
  }
}

/* ---- Core request -------------------------------------------------- */

/**
 * Low-level fetch wrapper. Attaches the Bearer token, parses JSON, and
 * normalizes errors into ApiError. On a 401 it clears the stored token so
 * the auth guard can bounce the user back to login.
 *
 * @param {string} path      e.g. "/api/championships"
 * @param {object} [options]
 * @param {string} [options.method]
 * @param {object} [options.body]    JSON-serializable body
 * @param {object} [options.query]   query params object
 * @param {AbortSignal} [options.signal]
 * @param {boolean} [options.auth=true] attach Bearer token
 */
export async function request(path, options = {}) {
  const requestGeneration = getSessionGeneration()
  const {
    method = 'GET',
    body,
    query,
    signal,
    auth = true,
    headers: extraHeaders,
    rawBody = false, // when true (e.g. FormData), send body as-is, no JSON encoding
    // Optional audit context. The observer can always see the method, path and
    // body; only the CALLER knows what the record looked like *before* the
    // write, so pass `{ entity, entityId, before, action }` to get a
    // before/after pair in the trail instead of intent-only.
    audit: auditMeta,
    /* Named response headers to attach under `__headers`. Opt-in; see the note
     * where it is applied. Only `/api/tam/units` uses it today. */
    exposeHeaders,
    responseType,
  } = options

  let url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`

  if (query && typeof query === 'object') {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') qs.append(k, v)
    }
    const s = qs.toString()
    if (s) url += `?${s}`
  }

  const headers = { Accept: 'application/json', ...extraHeaders }
  // JSON bodies get a Content-Type; raw bodies (FormData) must NOT — the browser
  // sets multipart/form-data with the correct boundary itself.
  if (body !== undefined && !rawBody) headers['Content-Type'] = 'application/json'

  if (auth) {
    const token = getToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    } else {
      // NEVER SEND AN UNAUTHENTICATED WRITE. Previously a missing token just
      // omitted the header and let the request go, so the write left the
      // browser, came back 401, and the rep's outcome depended entirely on
      // whether that call site happened to surface the error. A write must
      // succeed or fail LOUDLY — so this fails here, before the network, with
      // a message that says what actually happened.
      const err = new ApiError(
        'You are not signed in in this tab, so nothing was saved. Sign in and try again.',
        { status: 401, path },
      )
      err.notSignedIn = true
      // Still report it, so the trail records the attempt rather than losing it.
      if (method !== 'GET' && !path.startsWith('/api/audit')) {
        notifyWrite({ method, path, body: auditMeta?.redact ? '[redacted]' : body,
          meta: auditMeta || null, ok: false, status: 401, error: 'not signed in' })
      }
      throw err
    }
  }

  // The audit sink itself must not be audited, or the first write recurses.
  const observed = method !== 'GET' && !path.startsWith('/api/audit')
  const report = (extra) =>
    observed && requestGeneration === getSessionGeneration() &&
    notifyWrite({
      method,
      path,
      // `audit.redact` keeps a body out of the observer entirely. Used by the
      // password rotation route: the trail must record that a rotation
      // happened, never the credential that was set.
      body: auditMeta?.redact ? '[redacted]' : rawBody ? '[binary]' : body,
      meta: auditMeta || null,
      ...extra,
      ...(auditMeta?.redact ? { response: undefined, error: extra.ok ? undefined : 'Credential request failed (details withheld from audit).' } : {}),
    })

  let res
  try {
    res = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? (rawBody ? body : JSON.stringify(body)) : undefined,
      signal,
    })
  } catch (err) {
    if (err?.name === 'AbortError') throw err
    report({ ok: false, status: 0, error: 'Network error' })
    throw new ApiError('Network error — could not reach the server.', {
      status: 0,
    })
  }

  // Parse body defensively — some error responses are HTML, not JSON.
  const ct = res.headers.get('content-type') || ''
  let payload = null
  if (responseType === 'blob' && res.ok) {
    payload = await res.blob()
  } else if (ct.includes('application/json')) {
    payload = await res.json().catch(() => null)
  } else {
    const text = await res.text().catch(() => '')
    payload = text ? { message: text } : null
  }

  if (!res.ok) {
    if (res.status === 401 && auth && requestGeneration === getSessionGeneration()) clearToken()
    /* ⚠️ A MACHINE TOKEN IS NOT AN ERROR MESSAGE.
     *
     * `POST /api/champion-update` refuses an assignment with
     * `{"error":"reason_required","reason":"An assignment change must include a
     * non-empty `reason`. It is written to the permanent ledger."}` — and this
     * put the literal string **reason_required** on the page, because `error`
     * won unconditionally. The server had already written the sentence a human
     * needs; we were showing them the enum instead.
     *
     * So: prefer a prose field when the `error` is a bare token (snake_case /
     * kebab-case, no spaces). A token still shows when it is all there is,
     * which is better than nothing and is at least searchable. */
    const errRaw = payload?.error || ''
    const isToken = typeof errRaw === 'string' && errRaw.length > 0 && !/\s/.test(errRaw)
    const prose = proseOf(payload?.reason) || proseOf(payload?.detail) || proseOf(payload?.message) || ''
    const raw = (isToken && prose) ? prose : (errRaw || proseOf(payload?.message) || '')
    const msg =
      !raw || looksLikeMarkup(raw)
        ? res.status === 404
          ? `Not deployed on the backend yet (404 ${path})`
          : `Request failed (${res.status})`
        : raw
    report({ ok: false, status: res.status, error: msg })
    throw new ApiError(msg, { status: res.status, body: payload, path })
  }

  report({ ok: true, status: res.status, response: payload })

  /* ⚠️ OPT-IN HEADER EXPOSURE, FOR THE ONE CASE THAT NEEDS IT.
   *
   * `request()` returns the body and nothing else, which is right: a caller that
   * had the whole Response could start making its own decisions about status and
   * caching, and there would be two places that know how this API behaves.
   *
   * But `/api/tam/units` answers with `x-sr-units-measured-at`, deliberately, so
   * a client can ask "is this newer than me" about a 5-minute-cached table. That
   * is a real capability and dropping it would mean a stale table could never be
   * told from a fresh one. So: named headers only, opted into per call, attached
   * under `__headers` so no existing caller sees any change. A caller that does
   * not ask gets exactly what it got before. */
  if (exposeHeaders && exposeHeaders.length && payload && typeof payload === 'object'
      && !Array.isArray(payload)) {
    const h = {}
    for (const name of exposeHeaders) h[name] = res.headers.get(name)
    payload.__headers = h
  }
  return payload
}

/* ---- Endpoint helpers --------------------------------------------- */

export const api = {
  championshipContactCandidates: (id, {signal} = {}) => request('/api/championships/contact-candidates', {query:{ids:id},signal}),
  // Auth
  login: (username, password) =>
    request('/api/auth/login', {
      method: 'POST',
      auth: false,
      body: { username, password },
      audit: { redact: true, action: 'auth.login', after: { attempted: true } },
    }),
  // Token validation. The backend serves this as GET (POST 404s and silently
  // logs the user out on every reload) — must match.
  me: () => request('/api/auth/me', { method: 'GET' }),

  // Access control. `accessMe` and the team-roster admin routes are LIVE on
  // sr-portal-backend; acting-as and audit are still 404, which the client
  // treats as "not live" rather than an error. See RBAC.md for each contract.
  //
  // The role + assigned book for the signed-in user. This is what moves a
  // session off UNSCOPED and onto its real lane.
  accessMe: (opts) => request('/api/access/me', opts),
  // Assignable roles + which source the rep book is derived from. Exec-only.
  accessConfig: (opts) => request('/api/access/config', opts),

  // --- Team roster (EXEC ONLY, enforced server-side by requireExec) -------
  // The client's own can(TEAM_MANAGE) gate is UX; every one of these routes
  // re-decides from the verified JWT, so a devtools role flip buys nothing.
  team: (opts) => request('/api/admin/team', opts),
  // Uploaded identity documents and preview tokens never enter the client audit
  // observer. The server records its own authenticated, attributable receipt.
  teamClickupIdentities: ({signal} = {}) => request('/api/admin/team/clickup-identities', {signal}),
  teamClickupIdentityPreview: ({rawInput, inputSha256, expectedVersion}, {signal} = {}) =>
    request('/api/admin/team/clickup-identities/preview', {
      method: 'POST', body: {rawInput, inputSha256, expectedVersion}, signal, audit: {redact: true},
    }),
  teamClickupIdentityApply: ({rawInput, inputSha256, expectedVersion, previewToken, reason}) =>
    request('/api/admin/team/clickup-identities/apply', {
      method: 'POST', body: {rawInput, inputSha256, expectedVersion, previewToken, reason}, audit: {redact: true},
    }),
  teamAdd: (body) => request('/api/admin/team', {
    method: 'POST', body,
    audit: { entity: 'team-member', entityId: body?.id, action: 'team.create',
      redact: true, after: { id: body?.id, name: body?.name, email: body?.email, role: body?.role } },
  }),
  teamUpdate: (id, body, before) => request(`/api/admin/team/${encodeURIComponent(id)}`, {
    method: 'PATCH', body,
    audit: { entity: 'team-member', entityId: id, before },
  }),
  teamRemove: (id, before) => request(`/api/admin/team/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    audit: { entity: 'team-member', entityId: id, before },
  }),
  teamBook: (id, opts = {}) =>
    request(`/api/admin/team/${encodeURIComponent(id)}/book`, opts),
  teamBookSet: (id, book, before) => request(`/api/admin/team/${encodeURIComponent(id)}/book`, {
    method: 'PUT', body: { book },
    audit: { entity: 'team-book', entityId: id, before },
  }),
  // PER-USER PERMISSION GRANT (exec only) — the durable half of the "Manage
  // Assignments" and "Cheer Lane Delegation" toggles.
  //
  // ⚠️ THIS USED TO PATCH /api/admin/team/:id WITH A `permissions` ARRAY, AND
  // NOTHING WAS EVER SAVED. That route has no `permissions` field: it ignored
  // the key and answered 200, so Team Management flashed "granted ... on the
  // roster" for a grant that existed nowhere. The real store is
  // PUT /api/permissions/:userId (requireExec, ledgered), which takes BOOLEANS:
  // `{ canManageAssignments?, canRepairData?, canDelegateLane?, reason? }`.
  // Found 2026-10-29 while wiring the cheer lane toggle.
  teamPermissions: (id, grants, before) =>
    request(`/api/permissions/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: grants,
      audit: { entity: 'team-permissions', entityId: id, action: 'team.permissions.set', before },
    }),
  /** GET /api/permissions (exec): every roster member with their effective grants. */
  permissionsList: (opts) => request('/api/permissions', opts),
  /** GET /api/permissions/me: the caller's own grants, plus the cheer lane. */
  permissionsMe: (opts) => request('/api/permissions/me', opts),
  // The password itself is never audited — only the fact that a rotation
  // happened. `request()` would otherwise put the body in the audit `after`.
  teamPassword: (id, password) => request(`/api/admin/team/${encodeURIComponent(id)}/password`, {
    method: 'PUT', body: { password },
    audit: {
      entity: 'team-member', entityId: id, action: 'team.password.rotate',
      redact: true, after: { rotated: true },
    },
  }),
  // Server-side grant for taking control of an account. The client's own check
  // is UX; this is where the real authorization decision has to be made, and
  // it must return a short-lived scoped token the write routes verify.
  actingStart: (body) => request('/api/access/acting-as', { method: 'POST', body }),
  actingStop: (body) =>
    request('/api/access/acting-as/stop', { method: 'POST', body }),
  // Audit sink. Client-side writes here are a stub — the durable trail must be
  // written server-side on the same request that performs the write.
  auditWrite: (event) => request('/api/audit', { method: 'POST', body: event }),
  auditRead: (query, opts) => request('/api/audit', { query, ...opts }),

  // Data (all Bearer-protected)
  /* THE WHOLE UNIVERSE IN ONE REQUEST — a server-built snapshot. Replaces ~148
   * sequential paged calls; the paged helper below is kept as the fallback. */
  championshipsAll: ({ signal } = {}) => request('/api/championships/all', { signal }),
  championshipMarketAudit: () => request('/api/championships/market-audit'),
  championships: (opts) => request('/api/championships', opts),
  /* THE RECENT WINDOW — one request, not 128.
   *
   * `championships` above follows the cursor to exhaustion: ~128 sequential
   * calls to pull 12,740 records so the browser can filter down to the dozen
   * that concluded this weekend. This asks the server that question directly
   * and comes back in well under a second, with the detection run receipt
   * derived from champion_detected_at already on those records.
   *
   * It answers a different question from `championships`, so it does not
   * replace it — the map, the rep books and the filters still need the whole
   * plane. It just means opening the board no longer requires it. */
  championWeek: (days, opts) => request('/api/champion-week', { query: { days }, ...opts }),
  // Command Center write-backs (sr-portal-backend, JWT). champion-write records
  // the winner + confirmed date (deliberately does NOT set sr_champion_status —
  // the backend guards that trigger). champion-update writes the allowlisted
  // pursuit/rep/winner fields.
  championWrite: (body) => request('/api/champion-write', { method: 'POST', body }),
  championUpdate: (body) => request('/api/champion-update', { method: 'POST', body }),

  /* ── THE FIELD POLICY — what may be written, at which tier, by THIS caller.
   *
   * ⚠️ THIS ROUTE HAS BEEN LIVE AND UNREAD. It reports two tiers (23 operational
   * fields, 14 privileged), a `never` list, the champion lock's protected set,
   * and the calling session's own `mayWrite` array — and until this build
   * nothing in the client asked it. The drawer's field editor is driven entirely
   * off it, so a tier change server-side reaches the UI with no frontend edit.
   *
   * ⚠️ `?id=` IS IGNORED — measured. The response is identical for every record,
   * so this is a statement about the CALLER, not about a record. Per-record
   * state comes from the record. Read by `readFieldPolicy()` in
   * features/command-center/pursuit/fieldEdit.js, which is the only thing that
   * parses it. */
  championFieldPolicy: (opts) => request('/api/champion-fields', opts),

  /* ── ONE RECORD, READ DIRECTLY — the write confirmation.
   *
   * ⚠️ NOT `/api/championships/all`. That snapshot is stale-while-revalidate:
   * it returns what it already holds and rebuilds behind the caller, even with
   * `?force=1`. Confirming a write against it would report a good write as a
   * mismatch and a bad one as fine depending purely on timing — which is worse
   * than not confirming at all, because it looks like proof.
   *
   * This returns the record's 38 RAW HubSpot properties, unprojected, so it can
   * answer "what does this field say NOW" for a field the board's slim
   * projection does not even carry. */
  championshipRecord: (id, opts) => request(`/api/championship/${encodeURIComponent(String(id))}`, opts),

  /* ── CORRECT ONE FIELD, WITH A REASON THAT REACHES THE LEDGER.
   *
   * Same route as `championUpdate`, and deliberately a separate helper: this one
   * is always ONE field, always carries a reason, and always carries the audit
   * `before` pair. A privileged correction is `override: true` + reason (the
   * route's own contract), composed by `buildEditRequest()`.
   *
   * ⚠️ THE CLIENT GATE ON THIS SECURES NOTHING (hard rule 5). `champion-update`
   * re-decides the tier against the verified JWT and answers 403
   * `data_repair_locked` when the caller lacks exec or `cc_data_repair`, or 403
   * `automation_forbidden` for `champion_locked` from a non-human identity. The
   * UI draws what the policy reports and renders whatever the route says.
   *
   * ⚠️ AND THE RESPONSE IS ASYMMETRIC — read it with `readEditResult()`, never
   * by hand. A confirmed write is `{ ok: true, fields: [...] }` with NO `wrote`
   * key; a refusal is `{ ok: false, wrote: [], ... }` where `fields` is the list
   * it REFUSED. Reading `wrote` reports every real write as nothing; reading
   * `fields` reports every 403 as written. Both measured live 2026-09-14. */
  championFieldEdit: (body, { before } = {}) =>
    request('/api/champion-update', {
      method: 'POST',
      body,
      audit: {
        entity: 'championship',
        entityId: String(body?.id || ''),
        action: 'cc.champion.fieldEdit',
        before,
      },
    }),

  /* ══ THE PURSUIT SPACES — the same three shapes, on companies and deals ══
   *
   * Brian: "these all must have read, and write functionality."
   *
   * Eight HubSpot lines (police · fire · corporate · military · greek ·
   * government · cheer · rfp), all served from THIS portal by
   * sr-portal-backend. `letterwinner` and the legacy tracker lines are NOT
   * among them and the policy route answers 400 `unknown_line` for them by
   * name — they are written through the `/api/pursuits-update` proxy, which
   * reaches a different system entirely. */

  /* ── THE POLICY, PER LINE.
   *
   * Carries what `champion-fields` does NOT, and the two additions are the
   * reason a second hardcoded field list does not exist in this app:
   *
   *   specs        label, type and the real ENUM OPTIONS read from the portal's
   *                live schema. An enum typed free-hand is how `sr_segment`
   *                gets accepted by a route and rejected by HubSpot — 68 PSAL
   *                records went exactly that way.
   *   unavailable  [{ field, why }] — fields the policy declares that the portal
   *                does NOT hold. Drawn disabled with the reason, because a
   *                write to one is accepted here and silently dropped there.
   *   objectType   `companies` | `deals`. The client must not infer it.
   *   owner        `{ property }` — read, never assumed. It is one env var from
   *                changing, and on the RFP line the property it names is also
   *                in `unavailable`.
   *
   * Read by `readPursuitPolicy()` in
   * features/command-center/pursuit/pursuitEdit.js, which is the only thing
   * that parses it. */
  pursuitFieldPolicy: (line, opts) =>
    request(`/api/pursuit-fields?line=${encodeURIComponent(String(line || ''))}`, opts),

  /* ── ONE RECORD, READ DIRECTLY — the write confirmation.
   *
   * ⚠️ NOT `/api/pursuits`, which is cursor-paged. The route's own response
   * carries a `note` making the same point: confirming a write against a paged
   * list is timing noise dressed as proof. Returns `{ properties }` — the raw
   * unprojected HubSpot property bag, which is what a correction is compared
   * against, because the adapter TRANSFORMS on the way past (`state`
   * "Connecticut" → "CT" on the board, live on corporate today). */
  pursuitRecord: (line, id, opts) =>
    request(
      `/api/pursuit/${encodeURIComponent(String(line || ''))}/${encodeURIComponent(String(id))}`,
      opts,
    ),

  /* ── CORRECT ONE FIELD, WITH A REASON THAT REACHES THE LEDGER.
   *
   * ⚠️ READ THE RESPONSE WITH `readPursuitEditResult()`, NEVER BY HAND — and
   * note it does NOT share `champion-update`'s asymmetry. `wrote` is present in
   * BOTH directions and is the only key that means a change happened; `fields`
   * echoes the REFUSED field on a 403, so reading it reports every refusal as a
   * success. Measured live 2026-09-14 on all four outcomes.
   *
   * ⚠️ AND `ok: true` ALONE IS NOT ENOUGH either — the route answers 200 having
   * changed nothing when a blank lands in `ignored`.
   *
   * ⚠️ THE CLIENT GATE ON THIS SECURES NOTHING (hard rule 5). The route
   * re-decides the tier against the verified JWT and answers 403
   * `data_repair_locked` for a privileged field without the grant, or 403
   * `field_forbidden` for anything on the `never` list. */
  pursuitFieldEdit: (body, { entity, entityId, before } = {}) =>
    request('/api/pursuit-update', {
      method: 'POST',
      body,
      audit: {
        entity: entity || 'pursuit-field',
        entityId: String(entityId || body?.id || ''),
        action: 'cc.pursuit.fieldEdit',
        before,
      },
    }),
  // REASSIGN A REP. The one write the assignment engine's proposals lead to,
  // and the only thing that ever sets an owner — a human click, never the
  // engine (see features/command-center/assignments.js).
  //
  // It writes `sr_assigned_rep`, a DISPLAY-NAME string on the allowlist, and
  // deliberately NOT `hubspot_owner_id`: only 2 of the 15 reps resolve to a
  // HubSpot owner, so an owner-id write would silently drop 13 of them — and
  // the picker now offers the whole 34-person team (designers, cheer,
  // manufacturing), almost none of whom are HubSpot owners at all.
  //
  // `audit` carries the real from → to pair, so the trail records what changed
  // rather than only what was asked for.
  //
  // ⚠️ `reason` IS REQUIRED AND IS NOT OPTIONAL SUGAR. This call omitted it
  // from the day the assignment lock shipped, and the backend refuses an
  // assignment without one — `beginAssignmentChange()` in sr-portal-backend
  // answers 400 `reason_required` BEFORE touching HubSpot. So every rep
  // assignment made from the Command Center was rejected, `sr_assigned_rep`
  // stayed at 0% populated, and the only trace was a rejection row in
  // `assignment_ledger`. The server was right and the client was silent about
  // a field it never collected.
  //
  // It is a positional parameter rather than a key on an options bag on
  // purpose: a caller that forgets it now writes `assignRep(id, rep)` and
  // sends `reason: undefined`, which fails loudly at the call site in review,
  // instead of blending into an object literal.
  assignRep: (id, rep, reason, audit) =>
    request('/api/champion-update', {
      method: 'POST',
      body: { id, sr_assigned_rep: rep, reason },
      audit,
    }),

  /* ── THE REP TRADE. A HAND-OVER THAT HAS TO BE ACCEPTED.
   *
   * ⚠️ THIS IS NOT `assignRep` WITH NICER WORDS, AND THE DIFFERENCE IS THE
   * WHOLE POINT. `assignRep` moves `sr_assigned_rep` immediately: whoever
   * presses it has handed the record over whether or not the other person
   * agreed, which is the one-way throw Brian forbade — *"the buck doesn't get
   * passed."* These routes carry a PENDING state, so a proposal sits until the
   * receiver accepts or declines, and until then the proposer still holds the
   * record and it stays in their tiers.
   *
   * `repWorklist.js`'s trade control was disabled from the day it was written
   * with that exact reason on it, and the flag is flipped in the same commit as
   * these doors — the rule "Request an exec push" broke.
   *
   * ⚠️ EVERY WRITE HERE REQUIRES `reason` AND THE SERVER ANSWERS 400
   * `reason_required` WITHOUT IT. That is the same ledger requirement that
   * 400'd 9,774 assignments (measured 2026-09-30) when the Command Center
   * omitted it, so the UI
   * COLLECTS it rather than discovering it as an error.
   *
   * ⚠️ AND THE REFUSALS ARE RENDERED, NEVER SWALLOWED. 403 `not_on_roster`,
   * 403 `not_yours_to_offer`, 409 `championship_settled`, 409
   * `trade_already_pending`, 423 `trade_window_locked`, 400 `receiver_off_roster`
   * and 409 `already_owned` all answer `{ok:false, error, reason}` where
   * `reason` is a full human sentence. Read it off `err.body.reason` and show
   * it — a thrown ApiError carries the parsed body. Writing our own sentence
   * for a refusal the server already explained is how two surfaces start
   * disagreeing about why something is blocked.
   *
   * ⚠️ `onRoster: false` IS A DIFFERENT ANSWER FROM AN EMPTY INBOX, and the
   * response distinguishes them by carrying a `why` sentence. Collapsing the
   * two would tell somebody they have no trades pending when the truth is that
   * we cannot tell. */
  assignmentTrade: (opts) => request('/api/assignment-trade', opts),

  /* What the server says about ONE record: `{outcome, mayTrade, unsourced,
   * basis, date, settledBy, reason, stamp}`. `reason` is a full sentence and is
   * rendered verbatim. `unsourced` means the championship's date is projected
   * rather than sourced — see UNSOURCED_DATE_WARNING in repWorklist.js, which
   * must be on the control BEFORE the rep sends, not only in the answer. */
  tradeEligibility: (id, opts) =>
    request(`/api/assignment-trade/eligibility/${encodeURIComponent(String(id))}`, opts),

  tradePropose: (body) => request('/api/assignment-trade/propose', {
    method: 'POST',
    body,
    audit: {
      entity: 'assignment-trade',
      entityId: String(body?.id || ''),
      action: 'cc.trade.propose',
    },
  }),

  /* 207 is a REAL outcome on propose and 200 is not the only success: the
   * proposal landed and the record mirror did not. The caller reads `state`
   * rather than assuming. */
  tradeDecide: (body) => request('/api/assignment-trade/decide', {
    method: 'POST',
    body,
    audit: {
      entity: 'assignment-trade',
      entityId: String(body?.id || ''),
      action: 'cc.trade.decide',
    },
  }),

  /* Claiming is not trading: there is nobody to accept, because nobody is
   * assigned. It still takes a reason, and it still 409s `already_owned` if
   * somebody got there first — which is the honest answer to a race, not an
   * error to hide. */
  tradeClaim: (body) => request('/api/assignment-trade/claim', {
    method: 'POST',
    body,
    audit: {
      entity: 'assignment-trade',
      entityId: String(body?.id || ''),
      action: 'cc.trade.claim',
    },
  }),
  /* ── THE TRADE DESK. THE BOARD, NOT AN INBOX.
   *
   * ⚠️ THIS IS NOT `assignmentTrade` WITH A WIDER FILTER. Those routes are
   * REP-SCOPED and SINGLE-RECORD: `GET /api/assignment-trade` answers "what is
   * waiting for ME", so a manager calling it gets `onRoster:false` and an empty
   * inbox, and its store is keyed one-row-per-record so a whole-governing-body
   * offer cannot be represented in it at all.
   *
   * `GET /api/trade-desk` is the TEAM-WIDE board at any SCOPE — a whole body,
   * a body in one state, a body/state/sport, or one championship. It is
   * deliberately NOT filtered by viewer: Brian — *"a place where our team sees
   * the championship opportunities their coworkers are asking to trade"* — so
   * seeing somebody else's offer IS the feature. What IS per-viewer is the
   * `controls` array on each card, which the server computes.
   *
   * ⚠️ EVERY WRITE REQUIRES `reason` AND THE SERVER ANSWERS 400
   * `reason_required` WITHOUT IT. Same ledger requirement that 400'd 9,774
   * assignments (measured 2026-09-30), so the UI COLLECTS it rather than
   * discovering it as an error.
   *
   * ⚠️ AND THE REFUSALS ARE RENDERED, NEVER SWALLOWED. 403 `not_yours_to_offer`,
   * 403 `not_an_executive`, 403 `not_on_roster`, 409 `offer_stale`, 409
   * `size_changed_since_posting`, 409 `overlaps_an_open_offer`, 409
   * `window_closed_at_event`, 409 `counter_is_not_narrower`, 413
   * `claim_too_large`, 423 `trade_window_locked` and 503 `index_incomplete` all
   * answer `{ok:false, error, reason}` where `reason` is a full human sentence.
   * Read it off `err.body.reason` and show it — writing our own sentence for a
   * refusal the server already explained is how two surfaces start disagreeing
   * about why something is blocked.
   *
   * ⚠️ 207 IS A REAL OUTCOME ON `claim` AND `executive-move`. HubSpot has no
   * transaction across records, so a trade of 118 can stop part-way; 207 means
   * some moved and names exactly which. The caller must not read "not 200" as
   * "nothing happened". */
  tradeDesk: (opts) => request('/api/trade-desk', opts),
  /* 🔴 THE COVERAGE STRIP'S RECORD FEED. Added 2026-10-16 because the strip was
   * pointed at the wrong route and showed "0 of 0" on every tile.
   *
   * It read `championshipsFeed()` -> `GET /api/championships/feed`, which is
   * NOT a list of championships. It is the SEED/HEAL INGEST REPORT:
   * `{ ranAt, created, corrected, flagged, champions, refused }`. No `records`,
   * no `results`, no `rows` — so `coverageTwoWays` was handed an empty array
   * and, because the route answers 200, the page rendered a SUCCESSFUL read of
   * an empty universe rather than a refusal. Measured live 2026-10-16 against
   * backend 4135649: that response is 2.8 KB and holds one flagged record. The
   * tiles read 0 of 0 (measured 2026-10-16) and would have kept reading 0 of 0
   * with a perfectly healthy backend.
   *
   * ⚠️ `championshipsFeed` IS NOT REMOVED. It is the correct call for the
   * ingest report and other surfaces use it for exactly that. The defect was
   * the wiring, not the route.
   *
   * This returns four columns for every championship on the object
   * (15,865 measured 2026-10-16, walked to exhaustion over 159 pages) —
   * sr_assigned_rep,
   * sr_lead_rep, ready_for_artwork, sr_pursuit_status — with the index's own
   * `complete`/`why`, and 503s rather than answering 200 with an empty list, so
   * a failed read can only ever render as "Coverage is unavailable" and never
   * again as a coverage of zero. */
  tradeDeskCoverageFeed: (opts) => request('/api/trade-desk/coverage-feed', opts),
  /* ⭐ THE LINK INDEX (2026-10-19). Every trade — desk offers AND one-record
   * proposals — keyed to its record ids, governing body and the rep for its
   * body x state x sport unit, each one `linked` or `flagged` with the route
   * that fixes it. The worklist, the Command Center rows and the governing-body
   * page all read THIS, so the three can never disagree about which record is
   * under which trade. Read-only. */
  tradeDeskLinks: (opts) => request('/api/trade-desk/links', opts),
  tradeDeskTrail: (id, opts) =>
    request(`/api/trade-desk/trail/${encodeURIComponent(String(id))}`, opts),
  tradeDeskOffer: (body) => request('/api/trade-desk/offer', {
    method: 'POST', body,
    audit: { entity: 'trade-desk', entityId: String(body?.body || body?.recordId || ''),
      action: 'cc.trade.offer' },
  }),
  tradeDeskClaim: (body) => request('/api/trade-desk/claim', {
    method: 'POST', body,
    audit: { entity: 'trade-desk', entityId: String(body?.offerId || ''),
      action: 'cc.trade.claim' },
  }),
  tradeDeskWithdraw: (body) => request('/api/trade-desk/withdraw', {
    method: 'POST', body,
    audit: { entity: 'trade-desk', entityId: String(body?.offerId || ''),
      action: 'cc.trade.withdraw' },
  }),
  tradeDeskEscalate: (body) => request('/api/trade-desk/escalate', {
    method: 'POST', body,
    audit: { entity: 'trade-desk', entityId: String(body?.offerId || body?.id || ''),
      action: 'cc.trade.escalate' },
  }),
  /* ⚠️ ITS OWN DOOR, NOT A FLAG ON `claim`. An executive move is a distinct
   * authority — it can move an account out of somebody else's book and after
   * the championship — so it is a separate route with its own grant
   * (cc_rep_assign), its own ledger kind and the executive's name recorded
   * permanently. A boolean on the claim route would have hidden the most
   * consequential action on the desk inside the least consequential one. */
  tradeDeskExecutiveMove: (body) => request('/api/trade-desk/executive-move', {
    method: 'POST', body,
    audit: { entity: 'trade-desk', entityId: String(body?.offerId || body?.id || ''),
      action: 'cc.trade.executive_move' },
  }),
  championshipsFeed: (opts) => request('/api/championships/feed', opts),
  // Pursuit engines — proxied server-side to sr-pursuit-tracker (single-origin).
  pursuits: (query, opts) => request('/api/pursuits', { query, ...opts }),
  pursuitsUpdate: (body) => request('/api/pursuits-update', { method: 'POST', body }),
  // THE RECOGNITION / PURSUIT CREATE — a Community Hero nomination (police /
  // fire) or a Greek product pursuit, both modelled as a HubSpot DEAL
  // associated to the organization's company record.
  //
  // LIVE. The route ships on sr-portal-backend and re-decides governance
  // server-side: 201 created · 200 `{ duplicate: true }` for an idempotent
  // re-submit · **409 `hero_conflict`** when the department already has an
  // active hero (nothing created). Callers must handle the 409 off
  // `err.body.conflict` — a thrown ApiError carries the parsed body.
  // Structurally audited like every other non-GET (see setWriteObserver).
  recognitionDeal: (body) => request('/api/recognition-deal', {
    method: 'POST', body,
    audit: { entity: 'recognition-deal', entityId: body?.companyId },
  }),
  clickupAccounts: (opts) => request('/api/clickup/accounts', opts),
  // Customer Journey order feed. SOURCE OF TRUTH = the real order tasks in
  // ClickUp list 901000306889 (assignees are the real reps), brokered by the
  // backend. `list` is sent so the route never has to guess which list.
  clickupOrders: (opts = {}) =>
    request('/api/clickup/accounts', {
      ...opts,
      query: { list: CLICKUP_ORDERS_LIST_ID, ...(opts.query || {}) },
    }),
  clickupTasks: (opts = {}) =>
    request('/api/clickup/tasks', {
      ...opts,
      query: { list: CLICKUP_ORDERS_LIST_ID, ...(opts.query || {}) },
    }),
  // The HubSpot mirror of those orders (per-account = one deal). Fallback
  // source — needs a deals read route on sr-portal-backend.
  deals: (opts) => request('/api/hubspot/deals', opts),
  clickupTaskStatus: (body) =>
    request('/api/clickup/task-status', { method: 'POST', body }),

  /* -- THE JOURNEY WRITE PATH ---------------------------------------------
   * `journeyModel` carries the list's ORDERED statuses, which is what makes a
   * status picker possible without the client inventing a vocabulary. It also
   * reports whether the 7-group approval model is BOUND; while it is not,
   * approvals cannot be recorded at all and the UI says so rather than
   * offering a button that would 400.
   *
   * `journeyAdvance` moves one order's status. Every rule lives on the server —
   * the lock, the status vocabulary, the backward-move reason requirement, the
   * outstanding-change-request gate, and an echo check that refuses to report
   * success on a write ClickUp accepted but did not apply. The client's job is
   * to send the intent and render the refusal honestly, never to pre-judge it.
   *
   * A 409 here is usually the system working, not a failure. */
  /* THE COMMUNICATION & APPROVAL LANE. Live orders with the three build tracks
   * resolved (artwork→JCAD, roster & sizes, financials), sorted so the ones
   * where the CUSTOMER owes a decision come first. `?waiting=customer`,
   * `?phase=Production`, `?rep=`, `?all=1`. */
  journeyLane: (query, opts = {}) => request('/api/journey/lane', { query, ...opts }),
  journeyModel: (opts = {}) => request('/api/journey/model', opts),
  journeyAdvance: (body) => request('/api/journey/advance', {
    method: 'POST', body,
    audit: {
      entity: 'clickup-order', entityId: body?.taskId,
      action: 'journey.production.update', before: body?.from ? { status: body.from } : undefined,
    },
  }),
  journeyApprovals: (taskId, opts = {}) =>
    request(`/api/journey/approvals/${encodeURIComponent(taskId)}`, opts),
  price: (body) => request('/api/price', { method: 'POST', body }),

  /* -- THE REVIEW PORTAL --------------------------------------------------
   * Anchored on the ClickUp ORDER, because the trigger for a review is
   * physical delivery and delivery is the order's fact. Payment is never a
   * gate — see sr-portal-backend/review-timing.js for the whole rule.
   *
   * `reviewAsk` can legitimately answer 409. That is not a failure to handle
   * quietly: it means the customer is not holding the product yet, and the
   * UI must say so rather than retrying. */
  reviewQueue: (query, opts = {}) => request('/api/reviews/queue', { query, ...opts }),
  reviews: (query, opts = {}) => request('/api/reviews', { query, ...opts }),
  reviewAsk: (body) => request('/api/reviews/request', {
    method: 'POST', body,
    audit: { entity: 'review', entityId: body?.orderId, action: 'reviews.ask' },
  }),
  reviewFollowup: (id, body) => request(`/api/reviews/${encodeURIComponent(id)}/followup`, {
    method: 'POST', body: body || {},
    audit: { entity: 'review', entityId: id, action: 'reviews.followup' },
  }),
  reviewReceived: (id, body) => request(`/api/reviews/${encodeURIComponent(id)}/received`, {
    method: 'POST', body,
    audit: { entity: 'review', entityId: id, action: 'reviews.received' },
  }),
  reviewVerify: (id) => request(`/api/reviews/${encodeURIComponent(id)}/verify`, {
    method: 'POST', body: {},
    audit: { entity: 'review', entityId: id, action: 'reviews.verify' },
  }),
  reviewShelf: (query, opts = {}) => request('/api/reviews/shelf', { query, ...opts }),
  reviewUsage: (id, usageStatus, publishedUrl) =>
    request(`/api/reviews/${encodeURIComponent(id)}/usage`, {
      method: 'POST', body: { usageStatus, publishedUrl },
      audit: { entity: 'review', entityId: id, action: 'reviews.publish' },
    }),
  reviewScoreboard: (opts = {}) => request('/api/reviews/scoreboard', opts),
  /* SR's OWN destinations — one Google link, one Facebook page, one set of
   * handles, for the whole company. The customer is reviewing Southern
   * Recognition, not their school, so these never vary per account. */
  reviewLinks: (opts = {}) => request('/api/reviews/links', opts),
  reviewLinksSave: (links) => request('/api/reviews/links', {
    method: 'PUT', body: links,
    audit: { entity: 'review-links', action: 'reviews.links.set' },
  }),
  /* The pre-staged per-account sequence. A READ — nothing is sent, and the
   * rep mails it from their own client so it comes from them. */
  reviewDraft: (orderId, type, opts = {}) =>
    request('/api/reviews/draft', { query: { orderId, type }, ...opts }),
  reviewSuppress: (orderId, reason) => request('/api/reviews/suppress', {
    method: 'POST', body: { orderId, reason },
    audit: { entity: 'review-suppression', entityId: orderId, action: 'reviews.suppress' },
  }),
  reviewUnsuppress: (orderId) =>
    request(`/api/reviews/suppress/${encodeURIComponent(orderId)}`, {
      method: 'DELETE',
      audit: { entity: 'review-suppression', entityId: orderId, action: 'reviews.unsuppress' },
    }),

  // Artwork Studio (all on the JWT backend — sr-portal-backend)
  artworkGenerate: (body) => request('/api/artwork/generate', { method: 'POST', body }),
  // Owned-first logo finder (ESPN fallback + write-behind proposal).
  logoFind: (query, { signal } = {}) =>
    request('/api/hubspot/logo-find', { query, signal }),
  logoProposals: (opts) => request('/api/hubspot/logo-proposals', opts),
  logoApprove: (fileId) =>
    request('/api/hubspot/logo-approve', { method: 'POST', body: { fileId } }),
  logoReject: (fileId) =>
    request('/api/hubspot/logo-reject', { method: 'POST', body: { fileId } }),
  // Canva element library (read-only browse of existing designs).
  canvaStatus: () => request('/api/canva/status'),
  // ONE-CLICK CONNECT. Returns `{ url }` — the Canva consent screen. The backend
  // captures the rotating refresh token on its own callback, so nothing about
  // the OAuth ever touches client code (hard rule #3). This is the whole fix
  // when the element pickers report "manual only".
  canvaConnectUrl: () => request('/api/canva/oauth/start'),
  canvaItems: (query, { signal } = {}) =>
    request('/api/canva/items', { query, signal }),
  // Canva FOLDER navigator — drill into folders/subfolders (e.g. Side Shanks →
  // Football). Returns { breadcrumb, folders, items }. CONTRACT (Brian setup:
  // Canva folders organized by sport subfolder).
  canvaBrowse: (query, { signal } = {}) =>
    request('/api/canva/browse', { query, signal }),

  // Artwork Engine (rebuild) — all additive, JWT-brokered.
  // Housing catalog (real .3dm/optimized models, owned).
  artworkHousings: (query, { signal } = {}) =>
    request('/api/artwork/housings', { query, signal }),
  /* ⚠️ `championshipSearch` IS RETIRED — the route it named does not exist.
   * Measured live 2026-09-09: GET /api/championships/search → 404 "Cannot GET".
   * sr-portal-backend serves /api/championships, /championships/all, /schema,
   * /feed and /surface — there is never a search route. Every caller silently
   * fell back to six fictional DEMO records (two of them high schools), which
   * is how a fake championship could reach a real build. Callers now use
   * `championshipsAll` — the whole universe in one request — and match
   * client-side. The helper is DELETED rather than left pointing at a 404,
   * because a helper that exists is a helper somebody will call. */
  /* Upload an element to HubSpot File Manager (OWNED). Returns { fileId, url }.
   *
   * ⚠️ IT IS JSON, NOT MULTIPART, and this comment used to say the opposite.
   * The live route reads `{ name, kind, dataUrl }` off express.json and there
   * is no multipart parser in the service at all (sr-portal-backend server.js
   * @70178c5). `request()` only passes a body through untouched when `rawBody`
   * is set, which this helper never set — so the three call sites that built a
   * FormData were posting the string "{}" and taking a 400 every time.
   *
   * Encode with `features/artwork/ownedAsset.js` rather than by hand: it is the
   * one place that knows the route's shape, its accepted types and its size cap. */
  artworkUpload: (payload) =>
    request('/api/artwork/upload', { method: 'POST', body: payload,
      audit: { entity: 'artwork-asset', action: 'artwork.upload' } }),
  // Artwork Build record-of-record (Phase 4) — created BEFORE the render.
  artworkBuildCreate: (body) => request('/api/artwork/builds', { method: 'POST', body }),
  artworkBuildAttach: (id, body) => request(`/api/artwork/builds/${id}/renders`, { method: 'POST', body }),
  artworkBuildUpdate: (id, body) => request(`/api/artwork/builds/${id}`, { method: 'PATCH', body }),
  artworkBuilds: (query, { signal } = {}) => request('/api/artwork/builds', { query, signal }),
  // Optional photoreal finish pass (Phase 6) — metal/finish/lighting only,
  // locked layer masked. Hard-gated server-side.
  artworkPhotorealFinish: (body) => request('/api/artwork/photoreal-finish', { method: 'POST', body }),

  /* -- ARTWORK STUDIO — the render engine (sr-portal-backend 8269e8b) ------
   * The Studio drives every toggle off /config, so a new beast, Setting &
   * Output preset, scale or view appears in the UI with no frontend change.
   * `/render` creates the build record BEFORE the images and always answers
   * with the assembled prompts — `renderPending: true` means the record and
   * the prompts are complete and only the image call was skipped (the OpenAI
   * key lives on Railway), which is a state to SHOW, never an error.
   * Status moves are a HUMAN action: draft → proof → submitted → production. */
  /* The fetch button. One record, one click — diagnoses what this row needs
   * and resolves the company tie where that is the answer. Read-only. */
  championFetch: (id, { signal } = {}) =>
    request('/api/champion-fetch', { method: 'POST', body: { id }, signal }),

  /* THE RECONCILIATION READOUT — the one screen that answers "is the
   * universe clean?". Read-only; the backend route has no write path.
   *
   * ⚠️ IT PAGES THE WHOLE OBJECT (16,002 records measured 2026-10-18, ~55s
   * cold on that read) and is cached
   * server-side for 60s with its age published. `force` re-measures. Do not
   * poll it. */
  reconciliation: ({ force = false } = {}) =>
    request(`/api/reconciliation${force ? '?force=1' : ''}`, { method: 'GET' }),

  /* THE ROWS BEHIND ONE OF THOSE NUMBERS.
   *
   * ⚠️ READ THIS BEFORE "SIMPLIFYING" IT INTO A CLIENT-SIDE PREDICATE. The
   * seven reconciliation rows are defined ONCE, server-side, in
   * reconciliation-predicates.js, and this route returns the ids from the SAME
   * cached pass that produced the count on the row. Re-deriving the predicates
   * here would create a second definition free to drift from the first — which
   * is precisely how champion-fetch and the hunt queue came to hold opposite
   * views of one record while each stayed internally consistent, so nothing
   * surfaced the disagreement. The round trip is the point. */
  reconciliationRecords: (filter) =>
    request(`/api/reconciliation/records?filter=${encodeURIComponent(filter)}`, { method: 'GET' }),

  /* ADD A CHAMPIONSHIP OPPORTUNITY.
   *
   * Brian: "we should easily be able to add new championship opportunities."
   * The board can only work records it already has, so a governing body nobody
   * had entered was invisible however good the hunting got.
   *
   * IDEMPOTENT ON `sr_championship_key` — an existing key is SKIPPED, never
   * duplicated, which is why the key is derived rather than typed. `dryRun`
   * answers "does this already exist?" without writing.
   *
   * ⚠️ AND AS OF 2026-10-18 IT CANNOT MINT A BODYLESS RECORD. The backend
   * refuses a create naming no body in either field (422 body_required) and
   * reports those separately as `bodyBlocked` — never as `failed`, because a
   * refusal is the rule working. `toItems()` now sets `governing_body`. */
  championshipsLoad: (items, { dryRun = false } = {}) =>
    request('/api/championships/load', {
      method: 'POST', body: { items, dryRun },
      audit: { entity: 'championship', action: dryRun ? 'championship.create.dryrun' : 'championship.create' },
    }),

  /**
   * RUN THE REVIEWED DEFECT CORRECTIONS.
   *
   * The scope lives in `sr-portal-backend/repair-defects.js` as fixed
   * predicates — this call can RUN them, and narrow them to specific records,
   * but it cannot widen what any rule will touch. Passing `ids` is a request to
   * CONSIDER those records; each still has to match a rule to be changed.
   *
   * `dryRun` defaults to true on the server, so a forgotten flag is safe.
   */
  repairDefects: ({ dryRun = true, only = null, ids = null, limit = 100, after = null } = {}) =>
    request('/api/championships/repair-defects', {
      method: 'POST', body: { dryRun, only, ids, limit, after },
      audit: { entity: 'championship', action: dryRun ? 'championship.repair.dryrun' : 'championship.repair' },
    }),

  /** Backfill a MISSING championship key. Fill-only — it can never overwrite. */
  backfillKeys: ({ dryRun = true, limit = 200, after = null } = {}) =>
    request('/api/championships/backfill-key', {
      method: 'POST', body: { dryRun, limit, after },
      audit: { entity: 'championship', action: dryRun ? 'championship.key.dryrun' : 'championship.key.backfill' },
    }),

  /* REQUEST A HUNT — queue it for the FREE sweep.
   *
   * Brian: "the hand hunt on the subscription is the route every one of these
   * tasks must take."
   *
   * This is the button the Command Center presses. It does NOT call
   * /api/champion-hunt, which bills per record and is off by policy. It marks
   * the record `requested`; the 7am sweep hand-hunts on a subscription SR
   * already pays for and works requested records first.
   *
   * CC_CHAMPION_UPDATE, not SYSTEM_RUN — asking costs nothing, so a rep can do
   * it on their own book. Under the metered design they could not ask at all. */
  championRequest: (id, note) =>
    request('/api/champion-request', {
      method: 'POST', body: { id, note },
      audit: { entity: 'championship', entityId: id, action: 'champion.request' },
    }),

  /* ---- WHO IS THE CUSTOMER --------------------------------------------
   *
   * Brian: "once the command center finds the champions, we need to link
   * hubspot and find the actual customer."
   *
   * `POST /api/champion-company` has done exactly this since it was written
   * and NOTHING called it — there was no helper here at all, which is why the
   * door check could not flag it either: it counts helpers with no caller, and
   * this one had no helper to count.
   *
   * The route is deliberately TWO calls in one shape, and the split is the
   * whole safety property:
   *
   *   { id, champion }   LOOK. Returns match / company / candidates / reason
   *                      and writes NOTHING. Safe to run on any record.
   *   { id, companyId }  DECIDE. Associates the company a HUMAN picked.
   *
   * A wrong tie points a rep at the wrong customer, which is worse than an
   * untied record — so the server refuses to guess between close candidates
   * and hands them back for a person instead. These are separate helpers here
   * rather than one with an optional argument, because a single function whose
   * behaviour flips between "reads" and "writes" on the shape of its argument
   * is one typo from writing when somebody meant to look. */
  championCompanyLookup: (id, champion) =>
    request('/api/champion-company', { method: 'POST', body: { id, champion } }),

  championCompanyAttach: (id, companyId, reason) =>
    request('/api/champion-company', {
      method: 'POST', body: { id, companyId, reason },
      audit: { entity: 'championship', entityId: id, action: 'champion.company.tie' },
    }),

  /* RESOLVE — the five things that can be true about a championship.
   *
   * crowned · not_yet · split · not_found · manual. Built on the backend at
   * Brian's ask and never reachable from this app, which is why "two sources
   * disagree", "this event crowns several champions" and "I was there, let me
   * type it" all had nowhere to go.
   *
   * The refusals are the valuable part and they come back structured:
   *   409 champion_disagreement  { existing, proposed }  — never overwritten
   *   409 champion_locked        a human decided; automation does not un-decide
   *   400 invalid_resolution     { problems: [] }        — each one actionable
   *
   * `dryRun: true` on a split returns the PLAN instead of applying it. */
  championResolve: (body) =>
    request('/api/champion-resolve', {
      method: 'POST', body,
      audit: { entity: 'championship', entityId: body?.id, action: `champion.resolve.${body?.outcome || 'unknown'}` },
    }),

  /* ---- WITHDRAW AN UNSOURCED CROWN ------------------------------------
   *
   * Brian: correcting a bad crown has to be a control in the Command Center —
   * one authenticated call, from a phone, no terminal. Four wrong champions sat
   * live for a day because the only way to clear one was to dispatch a shell
   * run.
   *
   * ⚠️ THIS IS NOT A CHAMPION-ERASER, AND THE ROUTE IS WHY. There is no
   * per-record clear endpoint and deliberately so. `repair-defects` runs fixed
   * PREDICATES — `only: 'sourceless-crown'` matches a record that is crowned
   * AND carries no `champion_source_url` AND is not `champion_locked` AND has
   * not already been retracted. `ids` can only NARROW that set: passing an id
   * is a request to CONSIDER a record, never permission to change it. So the
   * authorization is the predicate, not the caller — which is what makes it
   * safe to put on a row.
   *
   * ⚠️ THE OTHER THREE ROUTES CANNOT EXPRESS THIS, and one of them fails
   * silently. `champion-update` drops a blank into `ignored` and answers
   * `ok:true` having changed nothing. `champion-write` 400s. `champion-resolve`
   * has no outcome meaning "there is no champion": `not_found` does not touch
   * the champion and `manual` requires one. Retraction had no expression
   * anywhere in the system until this rule.
   *
   * ⚠️ IT ANSWERS 200 ON A REFUSAL. Over the blast-radius cap, a degraded read,
   * an undeclared blank — all come back inside a 200 body as `capExceeded` /
   * `degradedRead` / `errors[]`, with NOTHING written. `written` is the only
   * field that means a change happened. See `readClearResult()` in
   * features/command-center/pursuit/clearCrown.js, which is the only thing that
   * reads this response, precisely so no component ever treats a 200 as a yes.
   *
   * SYSTEM_RUN on the backend (`permissions.js`), so exec + ops — NOT rep. The
   * client gate mirrors it rather than guessing wider; see ClearCrown.jsx.
   *
   * `before` carries the champion being removed. It is the single value most
   * worth having in the audit trail, and after the write nothing on the record
   * holds it except the retraction narrative and the ledger. */
  championsRepairDefects: (body, { before } = {}) =>
    request('/api/championships/repair-defects', {
      method: 'POST', body,
      audit: {
        entity: 'championship',
        entityId: Array.isArray(body?.ids) && body.ids.length === 1 ? String(body.ids[0]) : undefined,
        action: `championship.repair.${body?.only || 'all'}`,
        before,
      },
    }),

  /* THE SWEEP — "what still has nobody recorded as winning, in this window?"
   *
   * Brian: "I need a button that forces find championships from certain dates.
   * We are not inventing champions so we don't see into the future."
   *
   * The endpoint has existed since that ask (`resolve.js`, commented "the
   * button") and nothing in this app ever called it — so the button he asked
   * for was built on the server and never got a face.
   *
   * It is a READ. It returns the worklist ordered longest-failing-first and
   * writes nothing; the hunting is a separate, human-started step. The server
   * REFUSES a window ending in the future, because a championship that has not
   * happened can only return nothing or something invented. */
  championSweep: (body) =>
    request('/api/champion-sweep', { method: 'POST', body }),

  /* ⚠️ THE METERED HUNT HELPERS ARE DELIBERATELY ABSENT.
   *
   * Brian: "Nothing should be routed through paid api with Claude or OpenAI."
   *
   * `championHunt` / `championHuntJob` / `championHuntAccept` used to live here
   * and drove POST /api/champion-hunt, which bills per record. Two surfaces
   * called them — the row button and the selection bar — so one drag-select of
   * fifty rows was fifty paid searches behind a single click, with no estimate
   * and no ceiling in front of it. Deleting them is not tidying: as long as the
   * client can make that call, some future component will, and a spend guard
   * that lives on the far side of a network hop is a guard that has already
   * been reached.
   *
   * The route still exists on the backend, double-gated behind a key AND a
   * consent flag, for the day someone consciously turns it on with a budget.
   * Nothing in this app can reach it until the helper is deliberately restored.
   *
   * THE FREE ROUTE IS `championRequest`, defined above. */

  artworkStudioConfig: ({ signal } = {}) => request('/api/artwork/config', { signal }),
  artworkRender: (body) =>
    request('/api/artwork/render', {
      method: 'POST',
      body,
      audit: { entity: 'artwork-build', action: 'artwork.build.create' },
    }),
  artworkBuildGet: (buildId, { signal } = {}) =>
    request(`/api/artwork/build/${encodeURIComponent(buildId)}`, { signal }),
  artworkBuildStatus: (buildId, body, { before } = {}) =>
    request(`/api/artwork/build/${encodeURIComponent(buildId)}/status`, {
      method: 'POST',
      body,
      audit: { entity: 'artwork-build', entityId: buildId, before, action: 'artwork.build.status' },
    }),

  // Champion-to-Close seams.
  // Coach lookup (just-in-time from the coaches directory). CONTRACT-ONLY until
  // sr-portal-backend implements GET /api/hubspot/coach — see CHAMPION_TO_CLOSE.md.
  coach: (query, { signal } = {}) => request('/api/hubspot/coach', { query, signal }),
  // Outreach engine — one-click stage/send of the closing sequence. CONTRACT-ONLY
  // until the outreach engine exposes POST /api/outreach/stage.
  outreachStage: (body) => request('/api/outreach/stage', { method: 'POST', body }),
  /* The outreach ANGLES — the backend owns the list; the picker falls back to
   * local defaults only when this is unreachable, and says so on screen. */
  outreachAngles: ({ signal } = {}) => request('/api/outreach/angles', { signal }),
  outreachSequenceContext: (id,query,{signal}={}) => request(`/api/outreach/sequence-context/${encodeURIComponent(id)}`,{query,signal}),
  /* The scoreboard: which angle earns meetings. Read-only. */
  outreachEffectiveness: (query, { signal } = {}) => request('/api/outreach/effectiveness', { query, signal }),
  outreachCoverage: (query, {signal}={}) => request('/api/outreach/coverage',{query,signal}),
  outreachApprovalCapacity: (query,{signal}={}) => request('/api/outreach/approval-capacity',{query,signal}),

  /* ---- THE REP'S OWN MAILBOX ----------------------------------------------
   * Brian: "The build must include their email, as it sits in their outbox
   * drafts, not in mine. Each account is tied to each individual."
   *
   * ⚠️ AND THIS IS THE HALF THAT DECIDES WHETHER ANY OF IT IS REAL. The backend
   * routes exist and are deployed; this file is what lets a rep reach them. The
   * comment twenty lines above records this repo doing the opposite twice —
   * "working endpoint, no button" — and the outreach path is where it would cost
   * the most, because a rep with no Connect button simply cannot draft at all
   * and there is nothing on screen to tell them why.
   *
   * `repGmailStatus` is safe to poll: it returns booleans, the bound address and
   * the missing scopes, and never a token. */
  repGmailStatus: ({ signal } = {}) => request('/api/rep/google/status', { signal }),
  repGmailConnect: () => request('/api/rep/google/start', { method: 'POST' }),
  repGmailDisconnect: () => request('/api/rep/google/disconnect', { method: 'POST' }),
  /* Compose ONE draft into the signed-in rep's own Gmail. Draft only — the
   * backend has no send path (gmail-draft.js), so this cannot mail anybody. */
  outreachDraftToGmail: (body) =>
    request('/api/outreach/draft-to-gmail', {
      method: 'POST',
      body,
      audit: { entity: 'outreach-draft', action: 'outreach.draft.gmail' },
    }),
  /* The outcomes only a human can report: sent, reply, meeting. */
  outreachOutcome: (body) => request('/api/outreach/outcome', { method: 'POST', body }),
  outreachHistory: (id) => request(`/api/outreach/history/${encodeURIComponent(id)}`),
  /* Does the chain report end to end, and where not, why. Read-only. */
  outreachChain: (query, { signal } = {}) => request('/api/outreach/chain', { query, signal }),

  /* ---- THE STATE DIRECTORY LOAD -------------------------------------------
   * Loading a state's schools and coaches used to require pasting a Drive file
   * id and a bearer token into a browser console. The endpoints were live and
   * working for weeks with nothing in the product that could reach them — the
   * same "working endpoint, no button" gap that has bitten this platform twice
   * before. These helpers exist so the page can use the ONE audited request
   * path like every other write, rather than assembling fetches by hand.
   *
   * Every non-GET here goes through `request()`, so the write observer files an
   * audit event for it automatically. */
  /* TAM coverage — organizations AND the people attached, per state and
   * segment. Read-only aggregate counts; the ratio is the point, because an
   * organization with nobody attached cannot be sold to. */
  tamCoverage: ({ force = false, signal } = {}) =>
    request('/api/coverage/tam', { query: force ? { force: '1' } : undefined, signal }),

  /* ---- THE PIPELINE VALUE, AND THE MATH BEHIND IT -----------------------
   *
   * Brian: "Where is the pipeline value and the math behind it."
   *
   * `math=full` is always sent, because the decomposed chain IS the feature.
   * A caller that asked for the product alone would get a number with nothing
   * behind it, which is the thing this endpoint exists to stop.
   *
   * ⚠️ EVERY FIGURE IS `{ usd, basis, reason }` AND `usd` CAN BE null. A null is
   * "there is no basis for a figure here" and carries the reason; it must never
   * be coalesced to 0. See lib/tamChain.js — the presenter refuses to render a
   * null as a zero and the guard asserts it.
   *
   * ⚠️ A DENIAL IS NOT AN EMPTY RESULT. These routes answer 403 with
   * `render: "error-not-empty"` and a `detail` written for the person reading
   * it. Verified against production 2026-10-02T12:2x as a service identity:
   * `error: "rep_unmapped"`, detail "…It is NOT showing you nothing — it does
   * not know." That string is the whole point and it is rendered verbatim. */
  tamRecord: (id, { signal } = {}) =>
    request(`/api/tam/record/${encodeURIComponent(id)}`, { signal }),

  /* ⭐ THE UNITS TABLE — the one call that makes ranking a book by money
   * possible. `sport × level → { median, mean, p25, min, max, n }`, 218 rows,
   * backtested against production 2026-10-01: 0 rows missing `n`, 0 serving a
   * median of 0, 51 unmeasured rows each carrying a REASON, 0 rows carrying a
   * price. Total reconciles at 15,105.
   *
   * ⚠️ WHY THIS AND NOT `tamRecord` PER ROW. `/api/tam/record/:id` is one call
   * per record and the books run to 2,225, so ordering a whole book by dollars
   * was not possible with it. Re-ordering only the 30 rows urgency had already
   * chosen would have looked like the feature and not been it, so it was left
   * blocked until this existed.
   *
   * `cache-control: private, max-age=300, must-revalidate` — and deliberately
   * no stale-while-revalidate — so the browser caches it for five minutes and
   * revalidates rather than serving stale. `x-sr-units-measured-at` is asked
   * for explicitly so a client can tell a fresh table from a cached one. */
  /* 🔴 `?shape=envelope` IS LOAD-BEARING, NOT A PREFERENCE. The route's DEFAULT
   * response is a bare JSON array of rows, and a bare array has nowhere to put
   * a price — `readUnitsTable()` skips the price scan entirely on an array,
   * because a top-level key cannot exist on one. Asking for the envelope is
   * what makes `pricePerUnitUsd: 288` reachable at all; without it the table
   * loads perfectly and the view correctly reports that it has no price, which
   * is precisely the "no money on screen" state this release exists to end.
   *
   * ⚠️ AND THE HEADER IS ASKED FOR AS WELL, because it is served on BOTH
   * shapes. If the envelope is ever withdrawn, or an intermediary unwraps it,
   * the price still arrives rather than silently becoming null — the failure
   * mode here is not an error, it is a blank where a dollar figure should be,
   * and that is the kind that ships. */
  tamUnits: ({ signal } = {}) =>
    request('/api/tam/units?shape=envelope', {
      signal,
      exposeHeaders: ['x-sr-units-measured-at', 'x-sr-price-per-unit-usd'],
    }),
  /* ⭐ THE TEAM VIEW AND THE EXEC VISIBILITY TOGGLE — DECLARED NOW, because
   * THIS is the release that renders them. They sat undeclared on purpose:
   * "a helper with no control is not progress toward a feature; it is surface
   * area that reads as one." The person-switcher is the control.
   *
   * ⚠️ `/api/tam` IS SCOPE-ENFORCED SERVER-SIDE AND THE CLIENT MUST NOT
   * RE-DECIDE IT. `resolveScope()` adjudicates, and it returns one of three
   * denials with a sentence written for the person reading it:
   *
   *   rep_unmapped          your sign-in is not mapped to a rep on the records
   *                         — "It is not showing you an empty book, it cannot
   *                         identify one."
   *   team_visibility_off   a rep asked for someone else and the toggle is off
   *                         — "This is a setting, not an empty result."
   *   rep_unknown           the name asked for is not a rep on the records
   *
   * Every one of those is rendered VERBATIM. Exec always sees everything; the
   * toggle was only ever about reps seeing each other.
   *
   * `?rep=*` returns one row per rep with records AND modelledTamUsd, the
   * oversight/unassigned `holding`, both ranks and the divergence between them
   * — the count-and-value view. See the note in TEAM-ROLLUP-2026-10-04.md: that
   * is computed server-side and must be rendered, not recomputed here. */
  tamTeam: ({ rep = null, signal } = {}) =>
    request('/api/tam', {
      signal,
      query: { ...(rep ? { rep } : {}), math: 'full' },
    }),

  /* The exec toggle. ⚠️ `enabled` is sent as a real boolean: the writer accepts
   * only booleans and the literal strings 'true'/'false' and THROWS on anything
   * else, because `!!'false'` is `true` and a router written the obvious way
   * would have published every rep's book with `?enabled=false`. Confirmed
   * intact 2026-10-04 (`strictBool`, tam-visibility-store.js). */
  /* ⭐ THE TAM BASIS — MANAGER-ONLY, AND THE SERVER IS WHAT ENFORCES THAT.
   *
   * 🔴 THIS CLIENT GATE IS UX ONLY. `/api/tam/basis` is `money.read` and
   * `money.write` in the backend's RULES table, both held by exec alone, with a
   * second `isExec` lock inside each handler and the path on `MONEY_SURFACES`
   * so no machine identity can reach it either. Hiding the nav item is a
   * courtesy to a rep, not a security control, and neither action appears in
   * the client rbac matrix — they are in `SERVER_ONLY_ACTIONS` on purpose.
   *
   * ⚠️ A REFUSAL HERE IS A 403 WITH A SENTENCE, AND IT IS RENDERED VERBATIM.
   * Do not turn it into an empty table: "a rep cannot see a price point" and
   * "there are no rows" are different facts and must read differently.
   *
   * ⚠️ NO `?shape=` ON THIS ONE. Unlike `/api/tam/units`, whose default is a
   * pinned bare array, this route has always returned an envelope — it has to,
   * because the three totals, the coverage share and the thin-sample risk are
   * the point and none of them fit in a row. */
  tamBasis: ({ signal } = {}) => request('/api/tam/basis', { signal }),

  /* FBS bowl eligibility — the weekly board behind /bowl-eligibility.
   * The READ is CC_VIEW. The RUN re-reads the NCAA standings table and
   * replaces the current week's row; it is keyed on the week, so calling it
   * twice replaces rather than appends. It exists because a path that needs a
   * terminal is not a path — the weekly Railway job calls the same function.
   *
   * ⚠️ NO HELPER FOR `/api/fbs-bowl-eligibility/weeks`. The route exists and is
   * useful from a terminal, but nothing in the product calls it yet, and
   * check-doors.mjs is right to refuse a helper with no caller — a door with
   * no handle is how dead controls ship. Add it with the screen that needs it. */
  fbsBowlEligibility: ({ signal } = {}) => request('/api/fbs-bowl-eligibility', { signal }),
  /* The TRIGGERS — which teams crossed six wins, when, and whose desk they sit
   * on. Short by design: empty most weeks. This is the list outbound acts on,
   * and it is a separate read from the board because the board is 138 rows and
   * this is usually none. */
  fbsBowlTriggers: ({ signal } = {}) =>
    request('/api/fbs-bowl-eligibility/triggers', { signal }),
  fbsBowlEligibilityRun: ({ signal } = {}) =>
    request('/api/fbs-bowl-eligibility/run', { method: 'POST', body: {}, signal }),
  /* THE SPLIT-CONFERENCE SELECTOR (2026-10-26). The ledger read is scoped by
   * the server (a rep sees picks on their own desk); the write is exec-only
   * and re-checked server-side against Manage Assignments standing. */
  fbsBowlDeskPicks: ({ signal } = {}) =>
    request('/api/fbs-bowl-eligibility/desk-picks', { signal }),
  fbsBowlDeskPick: (body, { signal } = {}) =>
    request('/api/fbs-bowl-eligibility/desk-pick', { method: 'POST', body, signal }),
  /* THE BOWL COMMITTEE BOOK (2026-10-26). The committee is the buyer: John
   * Haggerty owns every bowl, one desk leader rides as the proposed second.
   * The assign write is exec-only and re-checked server-side; a step is
   * recorded by the people on that bowl, with its evidence. Neither writes a
   * HubSpot or ClickUp record. */
  bowlCommittees: ({ signal } = {}) => request('/api/bowl-committees', { signal }),
  bowlCommitteeWorklist: (person, { signal } = {}) =>
    request('/api/bowl-committees/worklist', { query: person ? { person } : undefined, signal }),
  bowlCommitteeAssign: (slug, body) =>
    request(`/api/bowl-committees/${encodeURIComponent(slug)}/assign`, {
      method: 'POST', body, audit: { entity: 'bowl-committee', entityId: slug, action: 'bowl.committee.assign' },
    }),
  bowlCommitteeStep: (slug, body) =>
    request(`/api/bowl-committees/${encodeURIComponent(slug)}/step`, {
      method: 'POST', body, audit: { entity: 'bowl-committee', entityId: slug, action: 'bowl.committee.step' },
    }),
  /* THE ROW ACTIONS AND THEIR WORKLIST (2026-10-26). Brian: "there is no way
   * to control any of it - like send an email or assign a rep when youve got 3
   * listed." Every write below is re-decided by the server: confirming the
   * account is exec only; contact, email and steps are the team's desk (lead +
   * pair partner) or an exec; a service identity is refused on all of them. */
  fbsBowlWorkOrders: ({ who = '', signal } = {}) =>
    request('/api/fbs-bowl-eligibility/work-orders', { query: who ? { who } : {}, signal }),
  fbsBowlAccountOptions: (team, { signal } = {}) =>
    request('/api/fbs-bowl-eligibility/account-options', { query: { team }, signal }),
  fbsBowlAccount: (body) =>
    request('/api/fbs-bowl-eligibility/account', { method: 'POST', body }),
  fbsBowlContacts: (team, { fresh = false, signal } = {}) =>
    request('/api/fbs-bowl-eligibility/contacts', { query: fresh ? { team, fresh: '1' } : { team }, signal }),
  fbsBowlContact: (body) =>
    request('/api/fbs-bowl-eligibility/contact', { method: 'POST', body }),
  /* Draft only, into the signed-in rep's own Gmail, through the same
   * createRepDraft the Champion-to-Close email uses. Nothing is sent. */
  fbsBowlEmailDraft: (body) =>
    request('/api/fbs-bowl-eligibility/email-draft', { method: 'POST', body }),
  fbsBowlStep: (body) =>
    request('/api/fbs-bowl-eligibility/step', { method: 'POST', body }),
  /* THE FUNNEL — eligible → owned → reached out → artwork → deal → closed, per
   * team, with the latency between stages.
   *
   * ⚠️ A SEPARATE READ FROM THE BOARD AND FROM /kpi, ON PURPOSE. This one
   * touches the effort log, the artwork build index and one live HubSpot
   * property to work out whether a stage is UNMEASURABLE or merely EMPTY, so it
   * is slower than the board. Behind its own call, its failure is reported on
   * its own and cannot make the board look empty.
   *
   * 🔴 Every stage it cannot compute returns null WITH its reason. The caller
   * must render that as words — never as a 0, a blank or a bare dash. */
  fbsBowlFunnel: ({ signal } = {}) =>
    request('/api/fbs-bowl-eligibility/funnel', { signal }),
  /* WHO LANDED WHAT - Jarvis (Claude) or Dot (ChatGPT), Brian 2026-10-26.
   * The stamp is applied by the server from the authenticated identity; this
   * only reads it. `recordIds` is capped at 25 by the route. */
  landings: ({ recordId, recordIds, agent, limit, signal } = {}) => {
    const q = new URLSearchParams()
    if (recordId) q.set('recordId', String(recordId))
    if (recordIds) q.set('recordIds', Array.isArray(recordIds) ? recordIds.join(',') : String(recordIds))
    if (agent) q.set('agent', String(agent))
    if (limit) q.set('limit', String(limit))
    const s = q.toString()
    return request(`/api/landings${s ? `?${s}` : ''}`, { signal })
  },
  /* Engine health with the agent that declared each last run. */
  engineHeartbeat: ({ signal } = {}) => request('/api/engine-heartbeat', { signal }),
  /* THE LAST HUNT (2026-10-26): when the champion hunt last ran, what it
   * landed, its last 7 nights, and whether it is overdue. Read-only. */
  huntLastRun: ({ days = 7, signal } = {}) => request(`/api/hunt/last-run?days=${encodeURIComponent(days)}`, { signal }),
  /* One row at a time, keyed sport × tier × award type — the SAME key the
   * offline workbook carries in column A, so a returned file reconciles
   * row-for-row instead of by fuzzy name match.
   *
   * ⚠️ `volume` and `priceUsd` of `null` CLEAR the override and let the row
   * fall back to the measured figure and the default price. They are NOT zeros:
   * the writer refuses a zero outright, because a zero price reads as free and
   * a zero volume reads as a championship worth nothing. An empty form control
   * must send null. */
  tamBasisSet: ({ sport, tier, award, volume, priceUsd, reason }) =>
    request('/api/tam/basis', {
      method: 'PUT',
      body: {
        sport,
        tier,
        award,
        volume: volume === '' || volume == null ? null : Number(volume),
        priceUsd: priceUsd === '' || priceUsd == null ? null : Number(priceUsd),
        reason: reason || null,
      },
      audit: {
        entity: 'tam-basis',
        entityId: `${sport}||${tier}||${award}`,
        action: 'tam.basis.set',
      },
    }),

  tamVisibility: ({ signal } = {}) => request('/api/tam/visibility', { signal }),
  tamVisibilitySet: (enabled, reason) =>
    request('/api/tam/visibility', {
      method: 'PUT',
      body: { enabled: enabled === true, reason: reason || null },
      audit: { entity: 'tam-visibility', entityId: 'team', action: 'tam.visibility.set' },
    }),
  /* Governing-body marks. The source URL is used ONCE, server-side, at ingest —
   * the bytes land in HubSpot File Manager and every surface renders the HubSpot
   * URL. Nothing here ever puts an association's own URL on the board. */
  brandMarks: ({ signal } = {}) => request('/api/brand/marks', { signal }),
  brandMarkIngest: (body) =>
    request('/api/brand/marks', {
      method: 'POST',
      body,
      audit: { entity: 'brand-mark', entityId: body?.key, action: 'brand.mark.ingest' },
    }),
  /* ---- THE BODY ROSTER: who works a governing body -----------------------
   *
   * Brian: "in governing body - i need to be able to add a rep to a governing
   * body." These three write the ASSIGNMENT MAP and never a record. The
   * backend derives the record writes separately, through apply-map, where a
   * human-set owner is reported as a conflict and never overwritten.
   *
   * ⚠️ `body` IS THE QUALIFIED `governing_body` STRING AND NOTHING ELSE — not
   * the registry key the card is keyed on, and never the bare acronym. "ACAA"
   * is the Alabama Christian Athletic Association (Chris, 22 records) AND the
   * Atlantic Collegiate Athletic Association (Zach, 11 Canadian records); the
   * server refuses the bare token for exactly that reason, and the control is
   * built to pass what the records actually carry.
   *
   * ⚠️ `authorizedBy` IS SENT EXPLICITLY AND IS NOT THE SESSION. The session is
   * who RAN it; `authorizedBy` is who DECIDED. They are the same person when a
   * manager clicks it themselves, which is the normal case — but the ledger has
   * to be able to tell the two apart, so it is a field and not an inference. */
  assignmentMap: ({ signal } = {}) => request('/api/assignments/map', { signal }),
  assignmentMapEntry: (body) => writeRoster(request, '/api/assignments/map-entry', body,
    { entity: 'governing-body', entityId: body?.body, action: 'assignment.body.roster.set' }),
  assignmentMapEntryRemove: (body) => writeRoster(request, '/api/assignments/map-entry/remove', body,
    { entity: 'governing-body', entityId: body?.body, action: 'assignment.body.roster.remove' }),
  coachesStates: ({ signal } = {}) => request('/api/coaches/states', { signal }),
  coachesLoadCapabilities: ({ signal } = {}) => request('/api/coaches/load-capabilities', { signal }),
  coachesLoadProgress: ({ signal } = {}) => request('/api/coaches/load-progress', { signal }),
  /* Creates-only and idempotent — adds the three company properties the
   * directory files need (athletic website, girls athletic phone, athletic
   * district alignment) and nothing else. Safe to press twice. */
  coachesEnsureProperties: (body = {}) =>
    request('/api/coaches/ensure-properties', {
      method: 'POST',
      body,
      audit: { entity: 'hubspot-schema', action: 'coaches.properties.ensure' },
    }),
  coachesLoadRun: (body) =>
    request('/api/coaches/load-run', {
      method: 'POST',
      body,
      audit: { entity: 'coaches-load', action: 'coaches.load.run' },
    }),
  coachesLoadStop: () =>
    request('/api/coaches/load-stop', {
      method: 'POST',
      audit: { entity: 'coaches-load', action: 'coaches.load.stop' },
    }),
  /* ======================================================================
   * THE GOVERNING-BODY CONTROLS — the doors behind "no dead-end flags".
   *
   * Brian, 2026-10-15: "if it's not something you can fix it, surface it but
   * I need a cta to actually fix it. Not just tell me and not have a place to
   * actually fix it."
   *
   * ⚠️ EVERY ONE OF THESE IS AN ENDPOINT THAT WAS ALREADY LIVE AND HAD NO
   * BUTTON. That is the finding, and it is worth stating rather than hiding
   * inside a diff: the Governing Bodies surface rendered ~91 problem signals
   * and could call exactly five API functions. The backend could already
   * stamp a verification, archive a record with its reason, create a
   * championship, rewrite `governing_body` and apply the assignment map —
   * nothing on the page could reach any of it. So this block is wiring, not
   * new capability, and it is why the fix did not need a backend deploy.
   *
   * ⚠️ AND THEY RUN AS THE SIGNED-IN HUMAN, WHICH IS THE POINT. Measured
   * 2026-10-15 against production as `svc:sr-gb-deadend-audit`:
   *   POST /api/assignments/apply-map   403 "limited to an executive or the
   *                                     named system actor"
   *   POST /api/assignments/map-entry   403 assignment_locked
   *   POST /api/repair/system/:op       403 "limited to an executive…"
   * A machine cannot do these at all. The browser session can, because it
   * carries Brian's identity — so one click here does what no engine in the
   * estate is permitted to do, and that is a feature of the design.
   * ====================================================================== */

  /** The verification split, whole object. Read. */
  verificationSummary: ({ signal } = {}) => request('/api/verification/summary', { signal }),
  /** The same per governing body — what makes "never verified" a card-level fact. */
  verificationBodies: ({ signal } = {}) => request('/api/verification/bodies', { signal }),
  /**
   * STAMP A BODY AS VERIFIED — up to 200 records per call.
   *
   * ⚠️ THE CAP IS THE SERVER'S AND IS NOT NEGOTIABLE HERE. `/stamps` returns
   * 413 above 200 because the gateway cuts at 30s and a batch killed mid-write
   * leaves a partially stamped object with no cursor. The caller chunks; see
   * `verifyBody()` in bodyControls.js, which keeps the cursor and reports the
   * per-chunk outcome rather than one optimistic total.
   */
  verificationStamps: (stamps) =>
    request('/api/verification/stamps', {
      method: 'POST', body: { stamps },
      audit: { entity: 'verification', action: 'verification.stamp.batch' },
    }),

  /**
   * RULE RECORDS OUT OF SCOPE — by ARCHIVING them with a reason.
   *
   * ⚠️ THIS IS NOT A DELETE AND THE WORD MATTERS. Object 2-64425303 has no
   * scope field: every spelling of `sr_scope`, `sr_out_of_scope`,
   * `sr_exclusion_reason` was checked name-by-name against the live property
   * surface and none exists. Adding one is a human's call on a live object, so
   * until it exists the archive path IS the exclusion mechanism — recoverable,
   * auditable, and it takes the record out of the live denominator instead of
   * leaving it in the creation queue to be re-worked every wave.
   *
   * ⚠️ `dryRun` DEFAULTS TO TRUE, mirroring the server. Nothing is archived
   * until a human has read what would be.
   */
  championshipsArchive: ({ keys, reason, dryRun = true }) =>
    request('/api/championships/archive', {
      method: 'POST', body: { keys, reason, dryRun },
      audit: { entity: 'championship', action: dryRun ? 'championship.archive.dryrun' : 'championship.archive' },
    }),

  /**
   * APPLY THE ASSIGNMENT MAP — the follow-through that makes setting a roster
   * mean something.
   *
   * ⚠️ WITHOUT THIS THE ROSTER CONTROL WAS A DEAD END WITH A BUTTON ON IT.
   * `map-entry` writes who WORKS a body; it changes no record, and the panel
   * says so honestly ("0 records changed assigned rep"). A person who set the
   * roster to fix 80 unassigned records then watched nothing happen learned
   * that the control does not work. The map is the policy; this is the apply.
   *
   * ⚠️ `dryRun` DEFAULTS TO TRUE — the server's own default — and
   * `authorizedBy` is REQUIRED by the server for anything that overwrites
   * another rep. Both are passed through rather than defaulted away.
   */
  assignmentsApplyMap: ({ scope = 'all', limit = null, dryRun = true, reason, authorizedBy = null,
    tradeLanes = null, tradeBodies = null } = {}) =>
    request('/api/assignments/apply-map', {
      method: 'POST',
      body: { scope, limit, dryRun, reason, authorizedBy, tradeLanes, tradeBodies },
      audit: { entity: 'assignment-map', action: dryRun ? 'assignment.map.apply.dryrun' : 'assignment.map.apply' },
    }),

  assignmentsApplySelected: (body) => request('/api/assignments/apply-map/selected', {
    method: 'POST', body,
    audit: { entity: 'assignment-map', action: body.dryRun ? 'assignment.map.selected.preview' : 'assignment.map.selected.apply' },
  }),

  /** The named blank-fill / correction operations, and what each one touches. */
  repairSystemOperations: ({ signal } = {}) => request('/api/repair/system', { signal }),
  /**
   * RUN ONE NAMED OPERATION. There is no general-purpose write path here by
   * design — `operation` must be one the server already names.
   */
  repairSystemRun: (operation, { dryRun = true, limit = null, reason = null } = {}) =>
    request(`/api/repair/system/${encodeURIComponent(operation)}`, {
      method: 'POST', body: { dryRun, limit, reason },
      audit: { entity: 'championship', entityId: operation, action: dryRun ? 'repair.system.dryrun' : 'repair.system.run' },
    }),

  googleStatus: ({ signal } = {}) => request('/api/google/status', { signal }),
  googleConnectUrl: () => request('/api/google/oauth/start'),

  // HubSpot (brokered server-side — never called from the browser directly)
  schools: (q, { signal } = {}) =>
    request('/api/hubspot/schools', { query: { q }, signal }),
  hubspot: (subpath, opts) =>
    request(`/api/hubspot/${String(subpath).replace(/^\/+/, '')}`, opts),
}
